import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Shield, CheckCircle, Zap, MessageCircle, AlertCircle, CreditCard, Phone,
  Home as HomeIcon, Building2 as Building2Icon, Factory as FactoryIcon,
  HelpCircle,
  UtensilsCrossed, Hotel, Building2, Stethoscope, GraduationCap,
  ShoppingBag, ShoppingCart, ChefHat, Landmark, Pill, Scissors,
  Dumbbell, Film, Warehouse, Utensils, Truck, BedDouble, Coffee,
  PawPrint, Theater, Factory, Snowflake, FlaskConical, Shirt,
  TestTube, Car, Hammer, Construction, Zap as ZapIcon, Fuel, FileText,
  Boxes, Beer, Cpu, Milk, Wheat, Ship,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { ensureRazorpayClickable } from '../lib/razorpayUnblock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import EstimatedCostCard from './EstimatedCostCard';
import CouponBox from './CouponBox';
import AddressFields, { EMPTY_ADDRESS, isAddressValid, formatAddress } from './AddressFields';
import { trackFormSubmit, gtmLeadSubmit, gtmBeginCheckout, gtmPurchase } from '../lib/tracker';
import { usePricingConfig } from '../data/pricingConfig';
import { calculatePlanAmount, useServicePlans } from '../data/servicePlans';
import ServicePlanSelector from './ServicePlanSelector';
import PremisesAreaSelector from './PremisesAreaSelector';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Segment selector =====
const SEGMENTS = [
  { id: 'residential', label: 'Residential', icon: HomeIcon, desc: 'Homes & apartments' },
  { id: 'commercial',  label: 'Commercial',  icon: Building2Icon, desc: 'Shops, offices, hotels' },
  { id: 'industrial',  label: 'Industrial',  icon: FactoryIcon, desc: 'Factories & plants' },
];

// ===== Industry/field grids (mirrors servicesData industryFields) =====
const COMMERCIAL_FIELDS = [
  { label: 'Restaurants & Cafés', icon: UtensilsCrossed },
  { label: 'Hotels & Resorts', icon: Hotel },
  { label: 'Offices & Coworking', icon: Building2 },
  { label: 'Hospitals & Clinics', icon: Stethoscope },
  { label: 'Schools & Colleges', icon: GraduationCap },
  { label: 'Retail Stores', icon: ShoppingBag },
  { label: 'Shopping Malls', icon: ShoppingCart },
  { label: 'Cloud Kitchens', icon: ChefHat },
  { label: 'Banks & ATMs', icon: Landmark },
  { label: 'Pharmacies', icon: Pill },
  { label: 'Salons & Spas', icon: Scissors },
  { label: 'Gyms & Fitness', icon: Dumbbell },
  { label: 'Cinemas & Theatres', icon: Film },
  { label: 'Warehouses', icon: Warehouse },
  { label: 'Food Processing', icon: Utensils },
  { label: 'Logistics & Couriers', icon: Truck },
  { label: 'Hostels & PGs', icon: BedDouble },
  { label: 'Cafés & Bakeries', icon: Coffee },
  { label: 'Pet Clinics', icon: PawPrint },
  { label: 'Event Halls & Banquets', icon: Theater },
  { label: 'Other / Not Listed', icon: HelpCircle },
];

const INDUSTRIAL_FIELDS = [
  { label: 'Manufacturing Plants', icon: Factory },
  { label: 'Warehouses & Godowns', icon: Warehouse },
  { label: 'Cold Storage', icon: Snowflake },
  { label: 'Food Processing Units', icon: ChefHat },
  { label: 'Pharmaceutical Plants', icon: FlaskConical },
  { label: 'Textile Mills', icon: Shirt },
  { label: 'Chemical Plants', icon: TestTube },
  { label: 'Automotive Plants', icon: Car },
  { label: 'Steel & Metal Plants', icon: Hammer },
  { label: 'Cement Plants', icon: Construction },
  { label: 'Power Plants', icon: ZapIcon },
  { label: 'Oil & Gas Refineries', icon: Fuel },
  { label: 'Paper & Pulp Mills', icon: FileText },
  { label: 'Plastic & Rubber Units', icon: Boxes },
  { label: 'Distilleries & Breweries', icon: Beer },
  { label: 'Electronics & Semiconductor', icon: Cpu },
  { label: 'Dairy & Cattle Farms', icon: Milk },
  { label: 'Grain Silos & Storage', icon: Wheat },
  { label: 'Logistics & Distribution', icon: Truck },
  { label: 'Shipping & Ports', icon: Ship },
  { label: 'Other / Not Listed', icon: HelpCircle },
];

// Global pest rates & BHK area map are now DB-managed (see usePricingConfig).
// Baked-in defaults live in ../data/pricingConfig.js as an offline fallback.

const RealTimeCalculator = ({ isOpen, onClose, preselectedPest, lockedSegment, preselectedSegment }) => {
  const pricing = usePricingConfig();
  const pestCards = pricing.pests;
  const bhkOptions = pricing.bhkOptions;
  const globalMinPrice = pricing.globalMinPrice;
  const roundTo = pricing.roundTo;
  const [segment, setSegment] = useState(preselectedSegment || lockedSegment || 'residential');
  const [industryField, setIndustryField] = useState('');
  const [customIndustryName, setCustomIndustryName] = useState('');
  const [selectedPest, setSelectedPest] = useState('');
  const [selectedBhk, setSelectedBhk] = useState('');
  const [manualArea, setManualArea] = useState('');
  const [pestDescription, setPestDescription] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [displayPrice, setDisplayPrice] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const planPestCategory = ({ general: 'general-pest', 'bed-bugs': 'bed-bug', bedbugs: 'bed-bug', bedbug: 'bed-bug', cockroaches: 'cockroach', cockroach_control: 'cockroach', rats: 'rodent', rodents: 'rodent', mosquitoes: 'mosquito', ants: 'ant', lizards: 'lizard', spiders: 'spider', fleas: 'flea', silverfishs: 'silverfish', flies: 'fly' }[selectedPest] || selectedPest || 'general-pest');
  const servicePlans = useServicePlans(segment, planPestCategory);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [customDurationMonths, setCustomDurationMonths] = useState('2');

  // ---------- Coupon state ----------
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const selectedServicePlan = servicePlans.find((plan) => plan.id === selectedPlanId) || null;
  const planAmount = typeof calculatedPrice === 'number'
    ? calculatePlanAmount(calculatedPrice, selectedServicePlan, Number(customDurationMonths))
    : 0;
  const customPlanValid = !selectedServicePlan?.is_custom_duration
    || (Number(customDurationMonths) >= Number(selectedServicePlan.custom_min_months || 1)
      && Number(customDurationMonths) <= Number(selectedServicePlan.custom_max_months || 60));

  // Auto-remove coupon if the package total changes — user must re-apply.
  useEffect(() => {
    if (appliedCoupon && appliedCoupon.original_amount !== planAmount) {
      setAppliedCoupon(null);
      setCouponError('');
    }
  }, [planAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!servicePlans.length) return;
    if (!servicePlans.some((plan) => plan.id === selectedPlanId)) {
      const preferred = servicePlans.find((plan) => plan.code === 'one-time') || servicePlans[0];
      setSelectedPlanId(preferred.id);
      if (preferred.is_custom_duration) setCustomDurationMonths(String(preferred.custom_min_months || 1));
    }
  }, [servicePlans, selectedPlanId]);

  const numericPrice = typeof calculatedPrice === 'number' ? calculatedPrice : 0;
  const finalAmount = appliedCoupon ? appliedCoupon.final_amount : planAmount;
  const discountAmount = appliedCoupon ? appliedCoupon.discount : 0;
  const displayedPayableAmount = typeof calculatedPrice === 'number' && calculatedPrice > 0
    ? finalAmount
    : displayPrice;
  const priceSummaryLabel = selectedServicePlan && selectedServicePlan.code !== 'one-time'
    ? `${selectedServicePlan.name} Plan Total`
    : (appliedCoupon ? 'Total After Discount' : 'Estimated Cost');
  // Commercial/Industrial now show a real estimate when a facility type + area are chosen.
  const nonResHasPrice = segment !== 'residential' && typeof calculatedPrice === 'number' && calculatedPrice > 0;

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    setCouponError('');
    if (!code) { setCouponError('Please enter a coupon code'); return; }
    if (planAmount <= 0 || !customPlanValid) { setCouponError('Select a valid service plan and property size first'); return; }
    setCouponLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/coupons/validate`, {
        code,
        amount: planAmount,
        phone: phone || null,
      }, { timeout: 6000 });
      if (res.data?.valid) {
        setAppliedCoupon({
          code: res.data.code,
          discount: res.data.discount,
          final_amount: res.data.final_amount,
          original_amount: res.data.original_amount,
        });
        setCouponError('');
      } else {
        setAppliedCoupon(null);
        setCouponError(res.data?.message || 'Invalid coupon');
      }
    } catch (e) {
      setAppliedCoupon(null);
      setCouponError(e?.response?.data?.detail || 'Could not validate coupon. Try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  // Extra services (multi-service add-ons) — each is independent {pestId, bhk, manualArea}
  const [extraServices, setExtraServices] = useState([]);
  const priceEligibleAreaLimit = Math.max(1, Math.min(100000, Number(pricing.areaLimits?.[segment]) || 100000));

  // ----- Pricing helper used for primary + each extra row -----
  const priceForLine = (pestId, bhk, manual) => {
    const pest = pestCards.find((p) => p.id === pestId);
    if (!pest) return 0;
    if (pest.id === 'other') return 'Inspection Required';
    const area = manual
      ? parseInt(manual, 10)
      : (bhkOptions.find((b) => b.value === bhk)?.area || 0);
    if (!area || area <= 0) return 0;
    if (area > priceEligibleAreaLimit) return 'Custom Quote';
    let price = area * pest.rate;
    if (price < globalMinPrice) price = globalMinPrice;
    return Math.round(price / roundTo) * roundTo;
  };

  // ----- Commercial / Industrial unit rate (₹/sqft) for the selected facility type -----
  const nonResUnit = () => {
    const cfg = segment === 'commercial' ? pricing.commercial : segment === 'industrial' ? pricing.industrial : null;
    if (!cfg) return null;
    const t = (cfg.types || []).find((x) => x.label === industryField);
    return { rate: t ? (Number(t.rate) || 0) : 0, minPrice: Number(cfg.min_price) || 0 };
  };

  // Sum of extras only (numbers only — "Inspection Required" rows contribute 0)
  const extrasTotal = extraServices.reduce((sum, e) => {
    const p = priceForLine(e.pestId, e.bhk, e.manualArea);
    return sum + (typeof p === 'number' ? p : 0);
  }, 0);
  const primaryAreaSqft = manualArea
    ? parseInt(manualArea, 10)
    : (bhkOptions.find((b) => b.value === selectedBhk)?.area || 0);
  const isAreaOverLimit = primaryAreaSqft > priceEligibleAreaLimit || extraServices.some((service) => {
    const area = service.manualArea ? parseInt(service.manualArea, 10) : (bhkOptions.find((b) => b.value === service.bhk)?.area || 0);
    return area > priceEligibleAreaLimit;
  });

  // Apply a pre-selected pest when the dialog is opened with one
  useEffect(() => {
    if (isOpen && preselectedPest) {
      setSelectedPest(preselectedPest);
    }
  }, [isOpen, preselectedPest]);

  // Apply a pre-selected / locked segment on open
  useEffect(() => {
    if (!isOpen) return;
    const next = lockedSegment || preselectedSegment;
    if (next && ['residential', 'commercial', 'industrial'].includes(next)) {
      setSegment(next);
      // Force-clear residential-specific BHK & industry-specific field when switching
      if (next !== 'residential') {
        setSelectedBhk('');
      } else {
        setIndustryField('');
      }
    }
  }, [isOpen, lockedSegment, preselectedSegment]);

  // Real-time price calculation
  useEffect(() => {
    const calculatePrice = () => {
      // Commercial / Industrial: price = area × facility-type rate, floored at segment min.
      if (segment !== 'residential') {
        const unit = nonResUnit();
        const area = manualArea ? parseInt(manualArea, 10) : 0;
        if (!unit || !unit.rate || unit.rate <= 0) return 0; // "Other / Not Listed" → quote on call
        if (!area || area <= 0) return 0;
        if (area > priceEligibleAreaLimit || isAreaOverLimit) return 0;
        let p = area * unit.rate;
        if (p < unit.minPrice) p = unit.minPrice;
        return Math.round(p / roundTo) * roundTo;
      }

      const pest = pestCards.find(p => p.id === selectedPest);
      if (!pest) return 0;

      if (pest.id === 'other') return 'Inspection Required';

      // Get area from manual input or BHK
      const area = manualArea 
        ? parseInt(manualArea) 
        : bhkOptions.find(b => b.value === selectedBhk)?.area || 0;
      
      if (!area || area <= 0) return 0;
      if (area > priceEligibleAreaLimit || isAreaOverLimit) return 0;
      
      let price = area * pest.rate;
      if (price < globalMinPrice) price = globalMinPrice;
      price = Math.round(price / roundTo) * roundTo;

      // Add extras
      return price + extrasTotal;
    };

    const newPrice = calculatePrice();
    setCalculatedPrice(newPrice);
    
    // Animate price change
    if (typeof newPrice === 'number' && newPrice !== displayPrice && newPrice > 0) {
      const duration = 500;
      const steps = 20;
      const stepValue = (newPrice - (typeof displayPrice === 'number' ? displayPrice : 0)) / steps;
      let current = 0;

      const interval = setInterval(() => {
        current++;
        const currentDisplayPrice = typeof displayPrice === 'number' ? displayPrice : 0;
        const newValue = Math.round(currentDisplayPrice + (stepValue * current));
        setDisplayPrice(newValue);
        
        if (current >= steps) {
          setDisplayPrice(newPrice);
          clearInterval(interval);
        }
      }, duration / steps);

      return () => clearInterval(interval);
    } else if (newPrice === 'Inspection Required') {
      setDisplayPrice(newPrice);
    }
  }, [selectedPest, selectedBhk, manualArea, extrasTotal, displayPrice, pricing, segment, industryField, priceEligibleAreaLimit, isAreaOverLimit]);

  // Dynamic progress tracking
  useEffect(() => {
    const steps = [];
    let progressValue = 0;

    // Step 1: Service Selected
    if (selectedPest) {
      steps.push(1);
      progressValue = 25;
    }

    // Step 2: Property Selected
    if (selectedBhk || (manualArea && parseInt(manualArea) > 0)) {
      steps.push(2);
      progressValue = 50;
    }

    // Step 3: Details Filled (name, phone, city + address core fields)
    if (name && phone && city && isAddressValid(address)) {
      steps.push(3);
      progressValue = 75;
    }

    // Step 4: Ready to Submit
    if (name && phone && city && phone.length >= 10 && (selectedBhk || manualArea) && selectedPest && isAddressValid(address)) {
      steps.push(4);
      progressValue = 100;
    }

    setCompletedSteps(steps);
    setProgress(progressValue);
  }, [selectedPest, selectedBhk, manualArea, name, phone, city, address]);

  const handlePestSelect = (pestId) => {
    // Toggle: clicking the already-selected pest unselects it (UX-driven double-tap behaviour).
    setSelectedPest((prev) => (prev === pestId ? '' : pestId));
  };

  const handleBhkSelect = (bhkValue) => {
    setSelectedBhk(bhkValue);
    setManualArea(''); // Clear manual area when BHK selected
  };

  const handleManualAreaChange = (value) => {
    const normalized = Number.isInteger(Number(value)) ? Math.max(0, Math.min(100000, Number(value))) : 0;
    setManualArea(normalized > 0 ? String(normalized) : '');
    if (normalized > 0) {
      setSelectedBhk(''); // Clear BHK when manual area entered
    }
  };

  const handleSegmentChange = (next) => {
    if (lockedSegment) return; // locked by parent (commercial/industrial pages)
    if (next === segment) return;
    setSegment(next);
    setSelectedPlanId('');
    setCustomIndustryName('');
    // Reset segment-specific selections to avoid stale state
    if (next === 'residential') {
      setIndustryField('');
    } else {
      setSelectedBhk('');
    }
  };

  const isFormValid = () => {
    const baseValid = (
      selectedPest &&
      name &&
      phone &&
      phone.length >= 10 &&
      city &&
      isAddressValid(address)
    );
    if (!baseValid) return false;
    if (segment === 'residential') {
      return !!(selectedBhk || (manualArea && parseInt(manualArea) > 0));
    }
    // Commercial / Industrial — require area + industry field selection
    if (!industryField || !manualArea || parseInt(manualArea) <= 0) return false;
    // If "Other" picked, require the custom name
    if (industryField === 'Other / Not Listed' && !customIndustryName.trim()) return false;
    return true;
  };

  const isBookingReady = () => isFormValid() && customPlanValid;

  const buildLeadPayload = () => {
    const pest = pestCards.find(p => p.id === selectedPest);
    const propertySize = manualArea
      ? `${manualArea} sq ft`
      : bhkOptions.find(b => b.value === selectedBhk)?.label;
    const areaSqft = manualArea
      ? parseInt(manualArea)
      : bhkOptions.find(b => b.value === selectedBhk)?.area || null;

    // Multi-service line-items
    const extraLines = extraServices.map((e) => {
      const ep = pestCards.find((p) => p.id === e.pestId);
      const eArea = e.manualArea ? parseInt(e.manualArea, 10) : (bhkOptions.find((b) => b.value === e.bhk)?.area || null);
      const eSize = e.manualArea ? `${e.manualArea} sq ft` : (bhkOptions.find((b) => b.value === e.bhk)?.label || '');
      return {
        pest_type: ep?.id,
        pest_label: ep?.label,
        property_size: eSize,
        area_sqft: eArea,
        estimated_price: priceForLine(e.pestId, e.bhk, e.manualArea),
      };
    });

    return {
      pest,
      propertySize,
      areaSqft,
      payload: {
        name,
        phone,
        email: email || null,
        city,
        pest_type: pest.id,
        pest_label: pest.label,
        property_size: propertySize,
        area_sqft: areaSqft,
        estimated_price: typeof calculatedPrice === 'number' && calculatedPrice > 0 ? planAmount : 'Quote on Call',
        base_estimated_price: typeof calculatedPrice === 'number' && calculatedPrice > 0 ? calculatedPrice : null,
        service_plan: selectedServicePlan && typeof calculatedPrice === 'number' && calculatedPrice > 0 ? {
          id: selectedServicePlan.id,
          code: selectedServicePlan.code,
          name: selectedServicePlan.name,
          segment,
          duration_months: selectedServicePlan.duration_months,
          is_custom_duration: Boolean(selectedServicePlan.is_custom_duration),
          custom_duration_months: selectedServicePlan.is_custom_duration ? Number(customDurationMonths) : null,
          base_amount: calculatedPrice,
          plan_amount: planAmount,
        } : null,
        pest_description: pestDescription || null,
        segment,
        industry_field: industryField === 'Other / Not Listed'
          ? `Other — ${customIndustryName.trim()}`
          : (industryField || null),
        address: {
          state: address.state || '',
          district: address.district || '',
          city: address.city || city || '',
          line1: address.line1 || '',
          pincode: address.pincode || '',
          landmark: address.landmark || '',
        },
        additional_details: [
          additionalDetails,
          extraLines.length
            ? '\n— Additional services requested —\n'
              + extraLines.map((l, i) =>
                segment === 'residential'
                  ? `${i + 1}. ${l.pest_label} · ${l.property_size} · ₹${l.estimated_price}`
                  : `${i + 1}. ${l.pest_label} · ${l.property_size} · Quote on Call`,
              ).join('\n')
            : '',
        ].filter(Boolean).join('\n').trim() || null,
        source: 'calculator',
        extra_services: extraLines,
      },
    };
  };

  const persistLead = async () => {
    try {
      const { payload } = buildLeadPayload();
      const res = await axios.post(`${API_BASE}/leads`, payload, { timeout: 5000 });
      const leadId = res.data?.id || null;
      trackFormSubmit({ source: 'realtime_calculator', pest: payload.pest_type, has_coupon: !!appliedCoupon });
      // GA4 / GTM generate_lead — for ALL segments
      gtmLeadSubmit({
        leadId,
        segment: payload.segment,
        pestType: payload.pest_type,
        pestLabel: payload.pest_label,
        industryField: payload.industry_field,
        areaSqft: payload.area_sqft,
        value: typeof payload.estimated_price === 'number' ? payload.estimated_price : 0,
        city: payload.city,
        source: payload.source,
      });
      return leadId;
    } catch (err) {
      console.warn('Lead persistence failed', err?.message);
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!isBookingReady()) return;
    setIsRedirecting(true);

    const { pest, propertySize } = buildLeadPayload();
    const priceText = typeof calculatedPrice === 'number' ? `₹${finalAmount}` : calculatedPrice;
    const planLine = selectedServicePlan ? `Plan: ${selectedServicePlan.name}${selectedServicePlan.is_custom_duration ? ` (${customDurationMonths} months)` : ''}\n` : '';

    await persistLead();

    // Build multi-service line items for the WhatsApp message
    const extrasText = extraServices.length
      ? '\n— Additional Services —\n' + extraServices.map((e, i) => {
        const ep = pestCards.find((p) => p.id === e.pestId);
        const eArea = e.manualArea
          ? `${e.manualArea} sq ft`
          : (bhkOptions.find((b) => b.value === e.bhk)?.label || '—');
        if (segment !== 'residential') {
          return `${i + 2}. ${ep?.label || '—'} · ${eArea} · Quote on Call`;
        }
        const ePrice = priceForLine(e.pestId, e.bhk, e.manualArea);
        const ePriceText = typeof ePrice === 'number' ? `₹${ePrice}` : ePrice;
        return `${i + 2}. ${ep?.label || '—'} · ${eArea} · ${ePriceText}`;
      }).join('\n')
      : '';

    // Build industry line for non-residential — includes custom name if "Other"
    const industryLine = (() => {
      if (segment === 'residential' || !industryField) return '';
      if (industryField === 'Other / Not Listed' && customIndustryName.trim()) {
        return `Industry: Other — ${customIndustryName.trim()}\n`;
      }
      return `Industry: ${industryField}\n`;
    })();

    const message = `🌟 New ${segment.toUpperCase()} Pest Control Inquiry:

Name: ${name}
Phone: ${phone}
City: ${city}
Address: ${formatAddress(address) || '—'}
${industryLine}— Primary Service —
1. ${pest.label} · ${propertySize} · ${typeof calculatedPrice === 'number' && calculatedPrice > 0 ? `${priceText} (estimate)` : '(Quote on Call)'}
${planLine}${typeof calculatedPrice === 'number' && calculatedPrice > 0 && selectedServicePlan ? `BASE ESTIMATE: ₹${calculatedPrice}\nPACKAGE TOTAL: ₹${finalAmount}` : ''}
${extrasText}
${extraServices.length && segment === 'residential' ? `\nTOTAL ESTIMATE: ${priceText}` : ''}
${selectedPest === 'other' || (segment !== 'residential' && pestDescription) ? `\nPest Issue:\n${pestDescription}` : ''}
${additionalDetails ? `\nAdditional Details:\n${additionalDetails}` : ''}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=919410110910&text=${encodedMessage}`;

    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setIsRedirecting(false);

      setTimeout(() => {
        resetForm();
        onClose();
      }, 1000);
    }, 800);
  };

  const handlePayNow = async () => {
    if (!isBookingReady()) return;
    if (typeof calculatedPrice !== 'number' || calculatedPrice <= 0 || planAmount <= 0) {
      alert('Please select a valid pest service & property size first.');
      return;
    }
    if (typeof window.Razorpay !== 'function') {
      alert('Payment library is still loading. Please retry in a moment.');
      return;
    }

    setIsProcessingPayment(true);
    const leadId = await persistLead();

    // GA4 begin_checkout — fires before opening Razorpay (residential only as commercial/industrial don't pay here)
    {
      const _pest = pestCards.find(p => p.id === selectedPest);
      gtmBeginCheckout({
        leadId,
        segment,
        pestType: selectedPest,
        pestLabel: _pest?.label,
        value: planAmount,
        finalValue: finalAmount,
        coupon: appliedCoupon?.code || null,
      });
    }

    let order;
    try {
      const res = await axios.post(`${API_BASE}/payments/create-order`, {
        amount: calculatedPrice,  // legacy calculator base — backend applies the service plan securely
        name,
        phone,
        email: email || null,
        lead_id: leadId,
        coupon_code: appliedCoupon?.code || null,
        segment,
        service_plan_id: selectedServicePlan?.id || null,
        custom_duration_months: selectedServicePlan?.is_custom_duration ? Number(customDurationMonths) : null,
        notes: { city, pest_type: selectedPest, service_plan: selectedServicePlan?.name || 'One-Time' },
      }, { timeout: 10000 });
      order = res.data;
    } catch (err) {
      console.error('Create order failed', err);
      const msg = err?.response?.data?.detail;
      if (msg && typeof msg === 'string' && /coupon|discount|min/i.test(msg)) {
        setCouponError(msg);
        setAppliedCoupon(null);
      } else {
        alert(msg || 'Could not start payment. Please try again or use WhatsApp.');
      }
      setIsProcessingPayment(false);
      return;
    }

    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency || 'INR',
      name: 'EntoRid Pest Control',
      description: `Service Booking — ${selectedServicePlan?.name || 'One-Time'}`,
      order_id: order.order_id,
      image: typeof window !== 'undefined' ? `${window.location.origin}/favicon.ico` : undefined,
      prefill: {
        name,
        contact: phone,
        email: email || undefined,
      },
      notes: { city, lead_id: leadId || '' },
      theme: { color: '#00A86B' },
      method: {
        upi: true,
        card: true,
        netbanking: true,
        wallet: true,
      },
      config: {
        display: {
          // Prioritize UPI intent (GPay / PhonePe / Paytm) for India
          blocks: {
            upi: {
              name: 'Pay using UPI',
              instruments: [
                { method: 'upi', flows: ['intent', 'collect', 'qr'] },
              ],
            },
          },
          sequence: ['block.upi', 'block.others'],
          preferences: { show_default_blocks: true },
        },
      },
      modal: {
        ondismiss: () => setIsProcessingPayment(false),
        confirm_close: true,
        escape: true,
      },
      retry: { enabled: true, max_count: 3 },
      handler: async (response) => {
        try {
          await axios.post(`${API_BASE}/payments/verify`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            lead_id: leadId,
            email: email || null,
          });
          setPaymentSuccess(true);

          // GA4 purchase — fire AFTER successful verification so Google Ads attributes revenue
          {
            const _pest = pestCards.find(p => p.id === selectedPest);
            gtmPurchase({
              transactionId: response.razorpay_payment_id,
              leadId,
              segment,
              pestType: selectedPest,
              pestLabel: _pest?.label,
              value: planAmount,
              finalValue: finalAmount,
              coupon: appliedCoupon?.code || null,
              discount: discountAmount,
            });
          }

          // Auto-open WhatsApp thank-you message in new tab
          const pestObj = pestCards.find(p => p.id === selectedPest);
          const propertySize = manualArea ? `${manualArea} sq ft` : bhkOptions.find(b => b.value === selectedBhk)?.label;
          const thanksMsg = `Hi EntoRid! 👋\nMy payment of ₹${finalAmount} is successful.${appliedCoupon ? `\nCoupon Applied: ${appliedCoupon.code} (saved ₹${discountAmount})` : ''}\n\nName: ${name}\nPhone: ${phone}\nCity: ${city}\nService: ${pestObj?.label}\nProperty: ${propertySize}\n\nPlease confirm my booking schedule. Thank you!`;
          window.open(`https://api.whatsapp.com/send?phone=919410110910&text=${encodeURIComponent(thanksMsg)}`, '_blank');
        } catch (err) {
          console.error('Verify failed', err);
          alert('Payment received but verification failed. We will contact you shortly.');
        } finally {
          setIsProcessingPayment(false);
        }
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      // Belt-and-suspenders: ensure Razorpay's iframe is touch-clickable on
      // mobile even if Radix Dialog (or any other modal lib) re-applies
      // aria-hidden / pointer-events:none on its sibling nodes.
      const stopUnblock = ensureRazorpayClickable();
      const origDismiss = options.modal.ondismiss;
      options.modal.ondismiss = () => {
        try { stopUnblock(); } catch (_) { /* noop */ }
        if (origDismiss) origDismiss();
      };
      rzp.on('payment.failed', (resp) => {
        console.warn('Payment failed', resp?.error);
        try { stopUnblock(); } catch (_) { /* noop */ }
        setIsProcessingPayment(false);
      });
      rzp.open();
    } catch (err) {
      console.error('Razorpay open failed', err);
      setIsProcessingPayment(false);
    }
  };

  const resetForm = () => {
    setSelectedPest('');
    setSelectedBhk('');
    setManualArea('');
    setPestDescription('');
    setName('');
    setPhone('');
    setEmail('');
    setCity('');
    setAddress(EMPTY_ADDRESS);
    setAdditionalDetails('');
    setCalculatedPrice(0);
    setDisplayPrice(0);
    setPaymentSuccess(false);
    setCouponInput('');
    setAppliedCoupon(null);
    setCouponError('');
    setIndustryField('');
    setCustomIndustryName('');
    setSelectedPlanId('');
    setCustomDurationMonths('2');
    if (!lockedSegment) setSegment(preselectedSegment || 'residential');
  };

  const getProgressMessage = () => {
    if (progress === 100) return "Perfect! Ready to submit 🎉";
    if (progress >= 75) return "Almost done!";
    if (progress >= 50) return "You're halfway there!";
    if (progress >= 25) return "Great start!";
    return "Let's get started";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-[95vw] sm:w-full max-w-[95vw] sm:max-w-6xl p-0 gap-0 max-h-[92vh] sm:max-h-[95vh] overflow-hidden rounded-2xl sm:rounded-2xl border-0 sm:border shadow-2xl"
        data-testid="calc-dialog-content"
      >
        <DialogTitle className="sr-only">Instant pest control quote</DialogTitle>
        <DialogDescription className="sr-only">Select your service, protection plan, and booking details.</DialogDescription>
        <div className="flex flex-col md:grid md:grid-cols-2 md:min-h-[700px] max-h-[92vh] sm:max-h-[95vh] overflow-hidden">
          {/* LEFT PANEL — desktop only */}
          <div className="hidden md:flex relative bg-green-600 text-white p-12 flex-col justify-between overflow-hidden">
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            
            <div className="relative z-10">
              <h1 className="text-4xl font-bold mb-4 leading-tight">
                India&apos;s Most Trusted<br />Pest Control
              </h1>
              <p className="text-green-100 text-lg mb-8">
                Get instant pricing for professional pest control services
              </p>

              {/* Futuristic Real-time Price Display — residential only */}
              {isAreaOverLimit ? (
                <AreaLimitNotice limit={priceEligibleAreaLimit} testId="calc-area-limit-desktop" />
              ) : segment === 'residential' ? (
                <>
                  <EstimatedCostCard
                    price={displayedPayableAmount}
                    label={priceSummaryLabel}
                    basePrice={numericPrice}
                  />

                  {/* Coupon Box — directly under price, desktop */}
                  <div className="mt-4 relative" style={{ zIndex: 2 }}>
                    <CouponBox
                      couponInput={couponInput}
                      setCouponInput={(v) => { setCouponInput(v); if (couponError) setCouponError(''); }}
                      appliedCoupon={appliedCoupon}
                      couponError={couponError}
                      couponLoading={couponLoading}
                      baseAmount={numericPrice}
                      onApply={handleApplyCoupon}
                      onRemove={handleRemoveCoupon}
                      compact
                    />
                  </div>
                </>
              ) : (
                <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-testid="calc-nonres-banner-desktop">
                  {nonResHasPrice ? (
                    <>
                      <div className="text-xs uppercase tracking-wider text-white/80 font-semibold mb-2">{priceSummaryLabel} · {segment}</div>
                      <div className="text-4xl font-extrabold leading-tight mb-1" data-testid="calc-nonres-price-desktop">₹{displayedPayableAmount.toLocaleString('en-IN')}</div>
                      <p className="text-green-50 text-xs leading-relaxed mb-3">Based on your area, facility type, and selected protection plan. Final price confirmed after a free site assessment.</p>
                    </>
                  ) : (
                    <>
                      <div className="text-xs uppercase tracking-wider text-white/80 font-semibold mb-2">Custom Quote</div>
                      <div className="text-3xl font-bold leading-tight mb-3">
                        {segment === 'commercial' ? 'Commercial Quote' : 'Industrial Quote'}
                      </div>
                      <p className="text-green-50 text-sm leading-relaxed">
                        Rates depend on area, pest type, and treatment scope. Submit your details and our specialist will call within 30 mins with the exact quote.
                      </p>
                    </>
                  )}
                  <div className="mt-4 space-y-2 text-xs text-green-50">
                    <div className="flex items-center gap-2"><CheckCircle size={14} /> No upfront payment</div>
                    <div className="flex items-center gap-2"><CheckCircle size={14} /> Custom IPM plan</div>
                    <div className="flex items-center gap-2"><CheckCircle size={14} /> Audit-ready documentation</div>
                  </div>
                </div>
              )}
            </div>

            {/* Trust badges */}
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Shield size={18} />
                </div>
                <span className="text-sm">Government Approved Chemicals</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={18} />
                </div>
                <span className="text-sm">100% Eco-Friendly & Safe</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Zap size={18} />
                </div>
                <span className="text-sm">15+ Years Experience</span>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="bg-white relative flex flex-col flex-1 min-h-0 overflow-hidden">
            {/* MOBILE-ONLY sticky header */}
            <div className="md:hidden sticky top-0 z-50 bg-white border-b flex items-center justify-between px-4 py-3">
              <div>
                <h2 className="text-base font-bold text-gray-900 leading-tight">Get Instant Quote</h2>
                <p className="text-[11px] text-gray-500 leading-tight">Real-time pricing</p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-2 -mr-2 text-gray-500 hover:text-gray-900 active:scale-95 transition-transform"
                data-testid="calc-close-mobile"
              >
                <X size={22} />
              </button>
            </div>

            {/* Desktop close button */}
            <button
              onClick={onClose}
              className="hidden md:block absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors z-50"
              data-testid="calc-close-desktop"
            >
              <X size={24} />
            </button>

            {/* STICKY Progress Tracker */}
            <div className="sticky top-[56px] md:top-0 z-40 bg-white border-b shadow-md">
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 md:p-6">
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">{getProgressMessage()}</span>
                    <span className="text-xs sm:text-sm font-bold text-green-600">{progress}%</span>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                
                {/* Step indicators */}
                <div className="flex justify-between mt-3 md:mt-4">
                  {[
                    { num: 1, label: 'Service' },
                    { num: 2, label: 'Property' },
                    { num: 3, label: 'Details' },
                    { num: 4, label: 'Ready' }
                  ].map((step) => (
                    <div key={step.num} className="flex flex-col items-center">
                      <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center font-semibold text-xs md:text-sm transition-all duration-300 ${
                        completedSteps.includes(step.num)
                          ? 'bg-green-600 text-white scale-110'
                          : 'bg-gray-300 text-gray-600'
                      }`}>
                        {completedSteps.includes(step.num) ? <CheckCircle size={14} /> : step.num}
                      </div>
                      <span className={`text-[10px] md:text-xs mt-1 font-medium ${
                        completedSteps.includes(step.num) ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* SCROLLABLE Form Content */}
            <div
              className="overflow-y-auto flex-1 p-4 md:p-8 pb-28 md:pb-8 overscroll-contain"
              style={{ WebkitOverflowScrolling: 'touch' }}
              data-testid="calc-scroll-area"
            >
              {/* Header — desktop only (mobile shows in sticky top bar) */}
              <div className="mb-6 hidden md:block">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Get Instant Quote</h2>
                <p className="text-gray-600">Fill the form below and watch the price calculate in real-time</p>
              </div>

              {/* ===== SEGMENT SELECTOR (Residential / Commercial / Industrial) ===== */}
              <div className="mb-6" data-testid="calc-segment-selector">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">Service Type</h3>
                  {lockedSegment && (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      {lockedSegment === 'commercial' ? 'Commercial Page' : lockedSegment === 'industrial' ? 'Industrial Page' : ''}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {SEGMENTS.map((seg) => {
                    const SegIcon = seg.icon;
                    const isActive = segment === seg.id;
                    const isDisabled = lockedSegment && lockedSegment !== seg.id;
                    return (
                      <button
                        key={seg.id}
                        type="button"
                        onClick={() => handleSegmentChange(seg.id)}
                        disabled={isDisabled}
                        data-testid={`calc-segment-${seg.id}`}
                        className={`relative p-2.5 sm:p-3 rounded-xl border-2 text-center transition-all ${
                          isActive
                            ? 'border-green-600 bg-green-50 shadow-md'
                            : isDisabled
                            ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                            : 'border-gray-200 hover:border-green-300 hover:shadow-sm bg-white'
                        }`}
                      >
                        <div className={`w-9 h-9 sm:w-11 sm:h-11 mx-auto rounded-lg flex items-center justify-center mb-1.5 ${
                          isActive ? 'bg-green-600 text-white' : 'bg-emerald-50 text-emerald-600'
                        }`}>
                          <SegIcon size={20} strokeWidth={2.2} />
                        </div>
                        <div className={`text-xs sm:text-sm font-semibold ${
                          isActive ? 'text-green-700' : 'text-gray-800'
                        }`}>
                          {seg.label}
                        </div>
                        <div className="hidden sm:block text-[10px] text-gray-500 leading-tight mt-0.5">{seg.desc}</div>
                      </button>
                    );
                  })}
                </div>
                {segment !== 'residential' && (
                  <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="calc-non-residential-note">
                    For {segment} sites, rates depend on area & treatment scope — we&apos;ll call you with a custom quote.
                  </p>
                )}
              </div>

              {/* Pest Selection */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Select Pest Type</h3>
                  {!selectedPest && <AlertCircle size={20} className="text-orange-500" />}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {pestCards.map((pest) => (
                    <button
                      key={pest.id}
                      onClick={() => handlePestSelect(pest.id)}
                      data-testid={`calc-pest-${pest.id}`}
                      className={`p-4 rounded-xl border-2 transition-all text-center transform hover:scale-105 ${
                        selectedPest === pest.id
                          ? 'border-green-600 bg-green-50 shadow-md'
                          : 'border-gray-200 hover:border-green-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="text-3xl mb-2">{pest.icon}</div>
                      <div className={`text-sm font-medium ${
                        selectedPest === pest.id ? 'text-green-700' : 'text-gray-700'
                      }`}>
                        {pest.label}
                      </div>
                    </button>
                  ))}
                </div>

                {selectedPest === 'other' && (
                  <div className="mt-4">
                    <Label>Describe your pest issue</Label>
                    <Textarea
                      value={pestDescription}
                      onChange={(e) => setPestDescription(e.target.value)}
                      placeholder="Example: small insects in kitchen..."
                      rows={3}
                      className="mt-2"
                      data-testid="calc-pest-description-input"
                    />
                  </div>
                )}

                {/* Optional pest description for commercial/industrial (any pest) */}
                {segment !== 'residential' && selectedPest && selectedPest !== 'other' && (
                  <div className="mt-4" data-testid="calc-nonres-pest-desc">
                    <Label className="text-xs">Specific pest name / details (optional)</Label>
                    <Textarea
                      value={pestDescription}
                      onChange={(e) => setPestDescription(e.target.value)}
                      placeholder="Example: German cockroaches near walk-in chiller, drain area..."
                      rows={2}
                      className="mt-2"
                    />
                  </div>
                )}
              </div>

              {/* ===== INDUSTRY / FIELD GRID (commercial & industrial only) ===== */}
              {segment !== 'residential' && selectedPest && (
                <div className="mb-6" data-testid="calc-industry-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Select Your {segment === 'commercial' ? 'Business Type' : 'Industry'}
                    </h3>
                    {!industryField && <AlertCircle size={20} className="text-orange-500" />}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {(segment === 'commercial' ? COMMERCIAL_FIELDS : INDUSTRIAL_FIELDS).map((f) => {
                      const FIcon = f.icon;
                      const active = industryField === f.label;
                      return (
                        <button
                          key={f.label}
                          type="button"
                          onClick={() => setIndustryField(f.label)}
                          data-testid={`calc-field-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className={`p-2.5 rounded-xl border-2 text-left transition-all ${
                            active
                              ? 'border-green-600 bg-green-50 shadow-md'
                              : 'border-gray-200 hover:border-green-300 bg-white'
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-1.5 ${
                            active ? 'bg-green-600 text-white' : 'bg-emerald-50 text-emerald-600'
                          }`}>
                            <FIcon size={18} strokeWidth={2} />
                          </div>
                          <div className={`text-xs font-semibold leading-tight ${
                            active ? 'text-green-700' : 'text-gray-800'
                          }`}>{f.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  {/* Custom industry textarea — only when "Other / Not Listed" is picked */}
                  {industryField === 'Other / Not Listed' && (
                    <div className="mt-4" data-testid="calc-custom-industry-wrap">
                      <Label className="text-sm font-semibold text-gray-800">
                        Tell us your {segment === 'commercial' ? 'business' : 'industry'} name <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        value={customIndustryName}
                        onChange={(e) => setCustomIndustryName(e.target.value)}
                        placeholder={
                          segment === 'commercial'
                            ? 'e.g., Boutique Studio, Yoga Centre, Co-living…'
                            : 'e.g., Solar Panel Plant, Mining Site, Agro-processing…'
                        }
                        rows={2}
                        className="mt-2"
                        data-testid="calc-custom-industry-input"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">We&apos;ll customise an IPM program for your specific site.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Property Size */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {segment === 'residential' ? 'Property Size' : 'Premises Area'}
                  </h3>
                  {!selectedBhk && !manualArea && <AlertCircle size={20} className="text-orange-500" />}
                </div>

                {segment === 'residential' && (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {bhkOptions.map((bhk) => (
                        <button
                          key={bhk.value}
                          onClick={() => handleBhkSelect(bhk.value)}
                          data-testid={`calc-bhk-${bhk.value}`}
                          className={`p-4 rounded-lg border-2 transition-all transform hover:scale-105 ${
                            selectedBhk === bhk.value
                              ? 'border-green-600 bg-green-50 shadow-md'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <div className={`font-semibold mb-1 ${
                            selectedBhk === bhk.value ? 'text-green-700' : 'text-gray-900'
                          }`}>
                            {bhk.label}
                          </div>
                          {pricing.showBhkArea && <div className="text-sm text-gray-500">{bhk.area} sq ft</div>}
                        </button>
                      ))}
                    </div>

                    <div className="text-center text-sm text-gray-500 mb-3">OR</div>
                  </>
                )}

                <PremisesAreaSelector
                  value={manualArea}
                  onChange={handleManualAreaChange}
                  label={segment === 'residential' ? 'Custom Premises Area' : 'Premises Area *'}
                  testId="calc-area-selector"
                  maxArea={100000}
                />
                <p className="mt-2 text-xs text-slate-500" data-testid="calc-area-pricing-limit">Instant online pricing up to {priceEligibleAreaLimit.toLocaleString('en-IN')} sq. ft. Larger sites receive a tailored sales quote.</p>
              </div>

              {/* ===== Additional Services (multi-service) — all segments ===== */}
              {selectedPest && selectedPest !== 'other' && (selectedBhk || manualArea) && (
                <div className="mb-6" data-testid="extra-services-section">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Need to treat another pest too?</h3>
                      <p className="text-xs text-gray-500">
                        {segment === 'residential'
                          ? 'Add multiple services in one booking — each priced by its own area.'
                          : "Add as many pests as you need — we'll quote them together on the call."}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setExtraServices((arr) => [...arr, { pestId: '', bhk: '', manualArea: '' }])}
                      data-testid="extra-add-btn"
                      className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                    >
                      + Add Service
                    </Button>
                  </div>
                  {extraServices.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No additional services added.</p>
                  ) : (
                    <div className="space-y-3">
                      {extraServices.map((row, idx) => {
                        const linePrice = priceForLine(row.pestId, row.bhk, row.manualArea);
                        return (
                          <div
                            key={idx}
                            className="bg-slate-50 border border-slate-200 rounded-xl p-3"
                            data-testid={`extra-row-${idx}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <span className="text-xs font-semibold text-slate-600">Service #{idx + 2}</span>
                              <button
                                type="button"
                                onClick={() => setExtraServices((arr) => arr.filter((_, i) => i !== idx))}
                                className="text-rose-500 hover:text-rose-700 text-xs font-semibold"
                                data-testid={`extra-remove-${idx}`}
                              >
                                Remove
                              </button>
                            </div>
                            <div className={`grid grid-cols-1 ${segment === 'residential' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-2`}>
                              {/* Pest */}
                              <div>
                                <Label className="text-xs">Pest Type *</Label>
                                <select
                                  value={row.pestId}
                                  onChange={(e) => setExtraServices((arr) => arr.map((r, i) => i === idx ? { ...r, pestId: e.target.value } : r))}
                                  className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                                  data-testid={`extra-pest-${idx}`}
                                >
                                  <option value="">— select —</option>
                                  {pestCards.filter((p) => p.id !== 'other').map((p) => (
                                    <option key={p.id} value={p.id}>{`${String(p.icon || '')} ${String(p.label || '')}`.trim()}</option>
                                  ))}
                                </select>
                              </div>
                              {/* BHK — residential only */}
                              {segment === 'residential' && (
                                <div>
                                  <Label className="text-xs">Property Size *</Label>
                                  <select
                                    value={row.bhk}
                                    onChange={(e) => setExtraServices((arr) => arr.map((r, i) => i === idx ? { ...r, bhk: e.target.value, manualArea: '' } : r))}
                                    className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                                    data-testid={`extra-bhk-${idx}`}
                                  >
                                    <option value="">— select —</option>
                                    {bhkOptions.map((b) => <option key={b.value} value={b.value}>{`${String(b.label || '')} (~${Number(b.area) || 0} sqft)`}</option>)}
                                  </select>
                                </div>
                              )}
                              {/* Fixed-step custom area */}
                              <div>
                                <Label className="text-xs">
                                  {segment === 'residential' ? 'Or Custom (sqft)' : 'Area (sqft) *'}
                                </Label>
                                <PremisesAreaSelector
                                  compact
                                  value={row.manualArea}
                                  onChange={(area) => setExtraServices((arr) => arr.map((r, i) => i === idx ? { ...r, manualArea: area > 0 ? String(area) : '', bhk: '' } : r))}
                                  label="Area"
                                  testId={`extra-area-${idx}`}
                                  maxArea={100000}
                                />
                              </div>
                            </div>
                            {/* Price line — residential only */}
                            {segment === 'residential' && row.pestId && (row.bhk || row.manualArea) && typeof linePrice === 'number' && (
                              <div className="mt-2 text-right text-sm" data-testid={`extra-price-${idx}`}>
                                Estimate for this service:{' '}
                                <span className="font-bold text-emerald-700">
                                  {typeof linePrice === 'number' ? `₹${linePrice.toLocaleString('en-IN')}` : linePrice}
                                </span>
                              </div>
                            )}
                            {row.pestId && (row.bhk || row.manualArea) && linePrice === 'Custom Quote' && (
                              <div className="mt-2 text-right text-xs font-semibold text-amber-700" data-testid={`extra-area-limit-note-${idx}`}>Custom quote required above {priceEligibleAreaLimit.toLocaleString('en-IN')} sq. ft.</div>
                            )}
                            {segment !== 'residential' && row.pestId && row.manualArea && (
                              <div className="mt-2 text-right text-xs text-emerald-700 font-semibold" data-testid={`extra-quote-tag-${idx}`}>
                                Quote on Call
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {segment === 'residential' && extrasTotal > 0 && (
                        <div className="text-right text-xs text-slate-500 font-medium" data-testid="extras-subtotal">
                          Add-ons subtotal: <span className="text-emerald-700 font-bold">₹{extrasTotal.toLocaleString('en-IN')}</span>
                          {' '}— rolled into the total below.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* User Details */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Your Details</h3>
                  {(!name || !phone || !city) && <AlertCircle size={20} className="text-orange-500" />}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      className={`mt-1 ${!name && completedSteps.length > 2 ? 'border-orange-300' : ''}`}
                      data-testid="calc-name-input"
                    />
                  </div>

                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                      className={`mt-1 ${(!phone || phone.length < 10) && completedSteps.length > 2 ? 'border-orange-300' : ''}`}
                      maxLength="10"
                      data-testid="calc-phone-input"
                    />
                    {phone && phone.length < 10 && (
                      <p className="text-xs text-orange-600 mt-1">Phone number must be 10 digits</p>
                    )}
                  </div>

                  <div>
                    <Label>City *</Label>
                    <Input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Enter your city"
                      className={`mt-1 ${!city && completedSteps.length > 2 ? 'border-orange-300' : ''}`}
                      data-testid="calc-city-input"
                    />
                  </div>

                  <div className="pt-2 border-t border-slate-100">
                    <AddressFields address={address} setAddress={setAddress} idPrefix="calc-addr" />
                  </div>

                  <div>
                    <Label>Email (Optional — for booking confirmation)</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="mt-1"
                      data-testid="calc-email-input"
                    />
                  </div>

                  <div>
                    <Label>Additional Details (Optional)</Label>
                    <Textarea
                      value={additionalDetails}
                      onChange={(e) => setAdditionalDetails(e.target.value)}
                      placeholder="Any specific requirements..."
                      rows={2}
                      className="mt-1"
                      data-testid="calc-additional-details-input"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              {paymentSuccess ? (
                <div
                  className="bg-gradient-to-br from-green-500 to-green-600 text-white py-6 rounded-xl text-center shadow-lg"
                  data-testid="payment-success-banner"
                >
                  <CheckCircle className="mx-auto mb-2" size={36} />
                  <div className="text-lg font-bold">Payment Successful!</div>
                  <div className="text-sm text-green-50 mt-1">Our team will contact you shortly to schedule the service.</div>
                  <button
                    type="button"
                    onClick={() => { resetForm(); onClose(); }}
                    className="mt-4 px-5 py-2 bg-white text-green-700 rounded-full font-semibold text-sm hover:bg-green-50"
                    data-testid="payment-success-close"
                  >
                    Close
                  </button>
                </div>
              ) : isRedirecting ? (
                <div className="bg-green-600 text-white py-4 rounded-lg text-center">
                  <div className="animate-pulse font-semibold">Redirecting to WhatsApp...</div>
                </div>
              ) : (
                <div className="space-y-3">
                  <ServicePlanSelector
                    plans={servicePlans}
                    selectedPlanId={selectedPlanId}
                    onSelect={(planId) => {
                      setSelectedPlanId(planId);
                      const plan = servicePlans.find((item) => item.id === planId);
                      if (plan?.is_custom_duration) setCustomDurationMonths(String(plan.custom_min_months || 1));
                    }}
                    customMonths={customDurationMonths}
                    onCustomMonths={setCustomDurationMonths}
                    baseAmount={numericPrice}
                  />
                  {/* Mobile-only inline price summary card — residential only */}
                  {segment === 'residential' && (
                    <div className="md:hidden mb-2 space-y-3">
                      {isAreaOverLimit ? <AreaLimitNotice limit={priceEligibleAreaLimit} testId="calc-area-limit-mobile" /> : <>
                        <EstimatedCostCard price={displayedPayableAmount} label={priceSummaryLabel} basePrice={numericPrice} />
                        <CouponBox couponInput={couponInput} setCouponInput={(v) => { setCouponInput(v); if (couponError) setCouponError(''); }} appliedCoupon={appliedCoupon} couponError={couponError} couponLoading={couponLoading} baseAmount={planAmount} onApply={handleApplyCoupon} onRemove={handleRemoveCoupon} compact />
                      </>}
                    </div>
                  )}
                  {segment !== 'residential' && (
                    <div className="md:hidden mb-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center" data-testid="calc-nonres-banner-mobile">
                      {isAreaOverLimit ? <AreaLimitNotice limit={priceEligibleAreaLimit} testId="calc-area-limit-nonres-mobile" compact /> : nonResHasPrice ? (
                        <>
                          <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">{priceSummaryLabel} · {segment}</div>
                          <div className="text-2xl font-extrabold text-emerald-700 mt-0.5" data-testid="calc-nonres-price-mobile">₹{displayedPayableAmount.toLocaleString('en-IN')}</div>
                          <div className="text-[11px] text-emerald-800 mt-1">Based on your selected protection plan; final price is confirmed after site assessment.</div>
                        </>
                      ) : (
                        <>
                          <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Custom Quote · {segment}</div>
                          <div className="text-sm text-emerald-800 mt-1">Submit your details &amp; we&apos;ll call with the exact price.</div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="hidden md:block">
                    {typeof calculatedPrice === 'number' && calculatedPrice > 0 ? (
                      <>
                        <Button
                          onClick={handlePayNow}
                          disabled={!isBookingReady() || isProcessingPayment || planAmount <= 0}
                          data-testid="calc-pay-now-btn"
                          className={`w-full h-14 text-lg font-semibold transition-all ${
                            isBookingReady() && planAmount > 0
                              ? 'bg-brand-blue hover:bg-brand-blue-light text-white shadow-lg hover:shadow-xl transform hover:scale-[1.01]'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          <CreditCard className="mr-2" size={20} />
                          {isProcessingPayment
                            ? 'Opening Razorpay…'
                            : (planAmount > 0
                                ? `Pay ₹${finalAmount.toLocaleString()} Now & Book`
                                : 'Pay Now')}
                        </Button>

                        <div className="flex items-center gap-3 my-3">
                          <div className="flex-1 h-px bg-gray-200" />
                          <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">or</span>
                          <div className="flex-1 h-px bg-gray-200" />
                        </div>
                      </>
                    ) : (
                      <Button
                        onClick={handleSubmit}
                        disabled={!isBookingReady() || isProcessingPayment}
                        data-testid="calc-submit-enquiry-btn"
                        className={`w-full h-14 text-lg font-semibold transition-all ${
                          isBookingReady()
                            ? 'bg-brand-blue hover:bg-brand-blue-light text-white shadow-lg hover:shadow-xl transform hover:scale-[1.01]'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <CheckCircle className="mr-2" size={20} />
                        {isFormValid() ? 'Submit Enquiry · We Call You' : 'Fill all fields to submit'}
                      </Button>
                    )}

                    {segment !== 'residential' && (
                      <div className="flex items-center gap-3 my-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">or</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={!isBookingReady() || isProcessingPayment}
                    data-testid="calc-whatsapp-btn"
                    variant="outline"
                    className={`w-full min-h-[48px] h-12 text-base font-semibold transition-all ${
                      isBookingReady()
                        ? 'border-green-600 text-green-700 hover:bg-green-50'
                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <MessageCircle className="mr-2" size={18} />
                    {isFormValid()
                      ? (typeof calculatedPrice === 'number' && calculatedPrice > 0 ? 'Chat on WhatsApp Instead' : 'Chat on WhatsApp')
                      : 'Fill all fields to continue'}
                  </Button>

                  <p className="text-xs text-center text-gray-500 mt-2 flex items-center justify-center gap-1">
                    <Shield size={12} className="text-green-600" />
                    {typeof calculatedPrice === 'number' && calculatedPrice > 0 ? 'Secure payments powered by Razorpay' : 'Our team will call within 30 mins'}
                  </p>
                </div>
              )}
            </div>

            {/* MOBILE-ONLY sticky bottom CTA */}
            {!paymentSuccess && !isRedirecting && (
              <div
                className="md:hidden sticky bottom-0 z-40 bg-white border-t border-gray-200 shadow-[0_-6px_18px_rgba(0,0,0,0.08)] px-4 py-3"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
                data-testid="calc-mobile-sticky-cta"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                      {isAreaOverLimit ? 'Sales Quote' : (typeof calculatedPrice === 'number' && calculatedPrice > 0 ? (appliedCoupon ? 'Total After Discount' : 'Plan Total') : 'Custom Quote')}
                    </div>
                    <div className="text-xl font-extrabold text-green-600 leading-tight truncate">
                      {typeof calculatedPrice === 'number' && calculatedPrice > 0
                        ? `₹${finalAmount.toLocaleString()}`
                        : (isAreaOverLimit ? 'Call Us' : (calculatedPrice === 'Inspection Required' ? 'Inspection' : 'On Call'))}
                    </div>
                    {appliedCoupon && (
                      <div className="text-[10px] text-green-700 font-semibold truncate">
                        {appliedCoupon.code} · saved ₹{discountAmount.toLocaleString()}
                      </div>
                    )}
                  </div>
                  {typeof calculatedPrice === 'number' && calculatedPrice > 0 ? (
                    <Button
                      onClick={handlePayNow}
                      disabled={!isBookingReady() || isProcessingPayment || planAmount <= 0}
                      data-testid="calc-pay-now-mobile-btn"
                      className={`min-h-[48px] h-12 px-5 text-sm font-semibold rounded-xl transition-all ${
                        isBookingReady() && planAmount > 0
                          ? 'bg-brand-blue hover:bg-brand-blue-light text-white shadow-lg active:scale-[0.98]'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <CreditCard className="mr-1.5" size={18} />
                      {isProcessingPayment ? 'Opening…' : 'Pay Now'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={!isBookingReady() || isProcessingPayment}
                      data-testid="calc-submit-mobile-btn"
                      className={`min-h-[48px] h-12 px-5 text-sm font-semibold rounded-xl transition-all ${
                        isBookingReady()
                          ? 'bg-brand-blue hover:bg-brand-blue-light text-white shadow-lg active:scale-[0.98]'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <CheckCircle className="mr-1.5" size={18} />
                      {isAreaOverLimit ? 'Get Quote' : 'Submit'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RealTimeCalculator;

function AreaLimitNotice({ limit, testId, compact = false }) {
  return <div className={`${compact ? 'p-3 text-left' : 'p-5'} rounded-lg border border-amber-300 bg-amber-50`} data-testid={testId}>
    <p className="text-sm font-extrabold text-amber-950">Contact Our Sales Team for the Best Price</p>
    <p className="mt-1 text-xs leading-relaxed text-amber-800">Areas above {limit.toLocaleString('en-IN')} sq. ft. receive a tailored custom quote.</p>
    <div className="mt-3 flex flex-wrap gap-2">
      <a href="tel:+917819988010" className="inline-flex items-center gap-1.5 rounded-md bg-amber-900 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-amber-950" data-testid={`${testId}-call-link`}><Phone size={14} /> Call Sales</a>
      <a href="https://api.whatsapp.com/send?phone=919410110910" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-amber-700 bg-white px-3 py-2 text-xs font-bold text-amber-900 transition-colors hover:bg-amber-100" data-testid={`${testId}-whatsapp-link`}><MessageCircle size={14} /> WhatsApp</a>
    </div>
  </div>;
}
